import { useEffect, useRef, useState } from "react";

import { useChartsContext } from "contexts/ChartContext";
import styled from "styled-components";

import { useChartEntities, useResizeObserver } from "hooks/charts";
import { setPointerOverChart } from "hooks/charts/echart/tooltipVisibility";
import { useWellStatusChartOptions } from "hooks/charts/useWellStatusChartOptions";
import { useWellStatusController } from "hooks/charts/useWellStatusController";

import { BusyIndicator } from "components/activity/shared";
import { ErrorBoundary } from "components/base";
import { IconSpinner } from "components/icons";

export const WELL_STATUS_ERROR_TEST_ID = "well-status-error-wrapper";

/**
 * ECharts host for both Well Status charts.
 *
 * A slim sibling of RigFracChart: the shared wells render path (EChartWrapper's
 * ~18 wells-specific hooks) is wells-production-bound, so only the chart-type
 * agnostic pieces are reused (echarts lifecycle, resize observer). Option building
 * + the screenshot transform live in useWellStatusChartOptions.
 */
function WellStatusChart() {
  const { id } = useChartsContext();
  const domRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<Nullable<echarts.ECharts>>(null);
  // The first response can land before the async echarts import resolves — this
  // flag re-runs the option effect once the instance exists.
  const [isInstanceReady, setIsInstanceReady] = useState(false);

  const { screenshot, dataTable } = useChartEntities(id);
  const { loading, error, apiResponse } = useWellStatusController();
  const isDataTableActive = dataTable?.active ?? false;
  const isScreenshotActive = screenshot?.active ?? false;

  // The failure overlay covers the canvas, so it may only appear when there is
  // genuinely nothing underneath it to look at. Two guards, both learned from QA
  // (product: "this shouldn't show up unless the chart truly
  // returned nothing"):
  //
  //  - NOT while a request is in flight. Changing a chart-focus field fires a
  //    burst of requests; one transient failure mid-burst would otherwise paint
  //    an outage banner (next to the spinner!) over a chart that is fine.
  //  - NOT when the last good response is still on screen. Keeping the previous
  //    bars is the useful behaviour; claiming the load failed on top of them is
  //    not.
  const hasRenderedData = (apiResponse?.series?.length ?? 0) > 0;
  const showFailure = Boolean(error) && !loading && !hasRenderedData;

  // Size the host to the preset while capturing so the chart fills the frame
  // instead of overflowing it.
  const preset = (screenshot?.properties?.preset ?? {}) as {
    width?: number;
    height?: number;
  };
  const screenshotWidth = isScreenshotActive ? preset.width : undefined;
  const screenshotHeight = isScreenshotActive ? preset.height : undefined;

  useEffect(() => {
    let isMounted = true;
    const chartDom = domRef.current;
    if (!chartDom) return;
    import("hd-echarts/echarts")
      .then((echarts) => {
        if (!domRef.current || !isMounted) return;
        chartInstanceRef.current = echarts.init(domRef.current);
        setIsInstanceReady(true);
      })
      .catch((err) => {
        throw new Error(`Error initializing hd chart instance:\n ${err.message}`);
      });

    // Hide the tooltip on pointer-leave with tooltip.show:false (EVA-6690).
    // HideTip/triggerOn alone are defeated for an `enterable` tooltip: a
    // re-render re-shows it via ECharts' _keepShow, and the cursor resting on the
    // tooltip sets _inContent, which makes hideLater skip the hide — so the
    // tooltip stayed stuck on widget→widget moves. Removing the component is the
    // only reliable dismissal; the shared pointer-over flag keeps it off across
    // notMerge rebuilds (useWellStatusChartOptions honours it).
    const setTooltipShown = (shown: boolean) => {
      const instance = chartInstanceRef.current;
      if (!instance || instance.isDisposed()) return;
      instance.setOption({ tooltip: { show: shown } });
    };
    const handleDomMouseEnter = () => {
      setPointerOverChart(id, true);
      setTooltipShown(true);
    };
    const handleDomMouseLeave = () => {
      setPointerOverChart(id, false);
      setTooltipShown(false);
      const instance = chartInstanceRef.current;
      if (instance && !instance.isDisposed()) {
        instance.dispatchAction({ type: "hideTip" });
      }
    };
    chartDom.addEventListener("mouseenter", handleDomMouseEnter);
    chartDom.addEventListener("mouseleave", handleDomMouseLeave);

    return () => {
      isMounted = false;
      chartDom.removeEventListener("mouseenter", handleDomMouseEnter);
      chartDom.removeEventListener("mouseleave", handleDomMouseLeave);
      if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domRef, id]);

  useResizeObserver(domRef, chartInstanceRef);
  useWellStatusChartOptions(chartInstanceRef, isInstanceReady);

  // Resize the canvas after a screenshot re-size (the ResizeObserver is
  // debounced; capture can fire before it settles).
  useEffect(() => {
    const instance = chartInstanceRef.current;
    if (instance && !instance.isDisposed()) instance.resize();
  }, [screenshotWidth, screenshotHeight, isInstanceReady]);

  return (
    <ErrorBoundary>
      <Wrapper
        onContextMenu={(e) => e.preventDefault()}
        className="ChartPlot"
        $screenshot={isScreenshotActive}
        $width={screenshotWidth}
        $height={screenshotHeight}>
        {/* Screenshot overlay portal target — same id convention as Chart.tsx */}
        <ScreenshotContainer id={`screenshot-container-${id}`} />
        <EChartsHost ref={domRef} id={id} data-testid={id} />
        {loading && (
          <BusyIndicator>
            <IconSpinner showBackground />
          </BusyIndicator>
        )}
        {showFailure && !isDataTableActive && (
          <ErrorMessageWrapper data-testid={WELL_STATUS_ERROR_TEST_ID}>
            Failed to load chart data
          </ErrorMessageWrapper>
        )}
      </Wrapper>
    </ErrorBoundary>
  );
}

export default WellStatusChart;

const Wrapper = styled.div<{
  $screenshot?: boolean;
  $width?: number;
  $height?: number;
}>`
  flex-grow: 1;
  position: relative;
  background-color: ${(p) => (p.$screenshot ? "#fff" : "inherit")};

  /* During capture the host must be EXACTLY the preset size. The min/max:100%
     constraints (used to fill the widget live) would otherwise let the grid
     parent's definite full height override the explicit preset height. */
  ${(p) =>
    p.$screenshot && p.$width && p.$height
      ? `
        flex: none;
        width: ${p.$width}px;
        height: ${p.$height}px;
        min-width: unset;
        min-height: unset;
        max-width: unset;
        max-height: unset;
      `
      : `
        width: 100%;
        height: 100%;
        min-width: 100%;
        min-height: 100%;
        max-width: 100%;
        max-height: 100%;
      `}
`;

const EChartsHost = styled.div`
  width: 100%;
  height: 100%;

  /* Scrollable tooltip when a focus yields many bins — same cap as the wells
     stacked-bar chart. Paired with the tooltip's enterable:true so the pointer
     can enter and scroll (ticket: "ability to scroll"). */
  .chart-tooltip-container {
    max-height: 250px;
    overflow-y: auto;
    /* Two CSS rules combine into a stray HORIZONTAL scrollbar:
         1. ECharts sizes the tooltip shrink-to-fit, so the box is exactly as
            wide as its widest row;
         2. overflow-x cannot stay "visible" while overflow-y is "auto" — the
            spec computes it to "auto" too.
       So the moment the rows exceed max-height, the vertical scrollbar takes
       ~15px out of the content box, the widest row no longer fits, and a
       horizontal scrollbar appears alongside it.

       scrollbar-gutter is the actual fix: it reserves that width INSIDE the
       intrinsic size, so the widest row still fits and no overflow arises. An
       earlier revision used overflow-x alone, which suppressed the scrollbar but
       genuinely clipped ~5px on platforms with classic (space-taking)
       scrollbars — and the clipped edge is the tail of the widest row, which on
       these tooltips is the end of the largest number. overflow-x stays as a
       backstop for anything the gutter cannot absorb.

       SCOPED TO THIS CHART ON PURPOSE. "stable" reserves the gutter whether or
       not the content overflows, so on platforms with space-taking scrollbars a
       short two-row tooltip carries ~15px of dead space. That is a fair trade
       here — this chart's tooltip is routinely long enough to scroll — but it is
       an app-wide visual change if applied to the shared hosts, which is not
       something a Well Status fix should decide for mosaic, probit, totalRateDate
       and the ml/sam charts. Those keep overflow-x alone and its ~5px clip. */
    scrollbar-gutter: stable;
    overflow-x: hidden;
  }
`;

const ScreenshotContainer = styled.div`
  position: absolute;
  display: grid;
  justify-content: center;
  pointer-events: none;
`;

const ErrorMessageWrapper = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(211, 211, 211, 0.424);
  z-index: 10;
  color: var(--color-text);
  font-weight: var(--fontWeightMedium);
`;
